/**
 * The application's data layer.
 *
 * The whole `S` object is fetched once and held in memory, then fed to the same engine
 * build the API uses. That is what preserves the feel of the original tool: editing a
 * threshold or a volume recalculates every tile immediately, with no round trip, and the
 * numbers are still guaranteed to match a server-side report because there is only one
 * engine.
 *
 * Writes are optimistic — local `S` first so the UI responds instantly, then a PATCH. If
 * the PATCH fails the state is refetched, so the screen can never keep showing a number
 * the server rejected.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createEngine } from '@capacitea/engine';
import { getAccessToken } from './auth.jsx';

const SCENARIO = 'live';

async function api(path, options = {}) {
  const token = await getAccessToken();
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? `Request failed (${res.status})`), {
      status: res.status,
      detail: body.detail,
    });
  }
  return res.status === 204 ? null : res.json();
}

const StateContext = createContext(null);

export function useModel() {
  const ctx = useContext(StateContext);
  if (!ctx) throw new Error('useModel must be used inside <ModelProvider>');
  return ctx;
}

export function ModelProvider({ children }) {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState(null);

  /**
   * The department currently in scope, shared across every view — the legacy build's global
   * `activeDept`. Picking Settlement in the sidebar and then opening Business Case should
   * show Settlement's business case, not reset to whatever is alphabetically first.
   * Session-scoped, so it does not persist past the tab.
   */
  const [activeDept, setActiveDeptState] = useState(() => {
    try { return sessionStorage.getItem('capacitea.activeDept') || 'ALL'; } catch { return 'ALL'; }
  });
  const setActiveDept = useCallback((id) => {
    setActiveDeptState(id);
    try { sessionStorage.setItem('capacitea.activeDept', id); } catch { /* private mode */ }
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['state', SCENARIO],
    queryFn: () => api(`/scenarios/${SCENARIO}/state`),
    staleTime: Infinity,
  });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api('/me') });

  // The optimistic local copy wins while a write is in flight; otherwise the server's.
  const state = overrides ?? data?.state ?? null;

  // Rebuilding the engine is cheap (it only closes over `S`), and doing it on every state
  // change is what keeps every derived number honest — there is no separate cache of
  // results that could drift from the inputs that produced them.
  const engine = useMemo(() => (state ? createEngine(state) : null), [state]);

  /** Applies a change locally, then persists it. */
  const patch = useCallback(
    async (collection, key, changes) => {
      const next = structuredClone(state);
      const record = next[collection]?.find((r) => r.id === key);
      if (!record) throw new Error(`No ${collection} record with id ${key}`);
      Object.assign(record, changes);
      setOverrides(next);

      try {
        await api(`/scenarios/${SCENARIO}/${collection}/${encodeURIComponent(key)}`, {
          method: 'PATCH',
          body: JSON.stringify(changes),
        });
        // Adopt the server's version as the new baseline rather than trusting the local copy.
        await queryClient.invalidateQueries({ queryKey: ['state', SCENARIO] });
        setOverrides(null);
      } catch (err) {
        setOverrides(null); // fall back to the last known-good server state
        throw err;
      }
    },
    [state, queryClient],
  );

  /**
   * Sets one key of a map-shaped part of the state (coverage cells, manager insight,
   * department volumes). Same optimistic-then-persist shape as `patch`, but these live in
   * dictionaries rather than arrays, so they cannot be addressed by record id.
   */
  const setMapValue = useCallback(
    async (mapName, key, value, body = value) => {
      const next = structuredClone(state);
      next[mapName] = { ...(next[mapName] ?? {}), [key]: value };
      setOverrides(next);

      try {
        await api(`/scenarios/${SCENARIO}/maps/${mapName}/${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: JSON.stringify(typeof body === 'object' && body !== null ? body : { value: body }),
        });
        await queryClient.invalidateQueries({ queryKey: ['state', SCENARIO] });
        setOverrides(null);
      } catch (err) {
        setOverrides(null);
        throw err;
      }
    },
    [state, queryClient],
  );

  const value = useMemo(
    () => ({
      state,
      engine,
      activeDept,
      setActiveDept,
      user: me?.user ?? null,
      isAdmin: (me?.user?.roles ?? []).includes('Admin'),
      isLoading,
      error,
      patch,
      setMapValue,
      api,
    }),
    [state, engine, me, isLoading, error, patch, setMapValue, activeDept, setActiveDept],
  );

  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

/**
 * Every department with its computed signal and severity, memoised on the current state.
 * This is the list nearly every view starts from.
 */
export function useDepartmentSignals() {
  const { state, engine } = useModel();
  return useMemo(() => {
    if (!state || !engine) return [];
    return state.depts.map((dep) => {
      const signal = engine.computeDeptSignal(dep);
      return { dep, signal, severity: engine.computeDeptSeverity(dep, signal) };
    });
  }, [state, engine]);
}

export { api };
